/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import {
  Eyebrow,
  Image,
  MODULE_LAYOUTS,
  ModuleLayout,
  getModuleLayoutConfig,
  isHorizontalLayout,
  isInvertedColor,
} from '@coursera-web/cds-labs';
import type { GridConfig, ImageType, ModuleLayoutConfig } from '@coursera-web/cds-labs';

import {
  AlwaysLightMode,
  Button,
  Chip,
  Grid,
  Hidden,
  Skeleton,
  TabContext,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import type { ButtonProps, Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type {
  CarouselFilter as CarouselFilterTrackingData,
  PageConfigComponent as PageConfigComponentTrackingData,
} from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { getIsSideBySideLayout } from 'bundles/cds-labs/components/Modules/utils/constants';
// External Shared Dependencies
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
// Eventing
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/cds-labs';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: ButtonProps['variant'] | null;
  showCtaArrowIcon?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const headerOrder = ['h2', 'h3', 'h4', 'h5', 'h6'] as const;

export type HeaderLevel = (typeof headerOrder)[number];

export type Filter = {
  id: string;
  filterName: string;
  filterImage?: ImageType | null;
  eventingData?: {
    carouselFilter?: CarouselFilterTrackingData;
  };
};

export type Props = {
  headline?: string | null;
  headlineComponent?: HeaderLevel;
  description?: React.ReactNode;
  showAllEntries?: boolean | null;
  filters?: Filter[] | null;
  collections?: CarouselCollection[];
  isCollectionsLoading?: boolean;
  onFilterChange?: (index: number, filterName: string) => void;
  currentCollectionSetIndex?: number;
  numOfEntriesPerCollection?: number;
  cta?: CTA;
  customCtaButton?: React.ReactNode;
  customChipStyles?: SerializedStyles;
  productCardTrackingName?: string | null;
  // show filters even if there is only one filter search experiment
  alwaysShowChipFilters?: boolean;
  eventingData?: {
    searchQuery?: string;
    page?: string;
    filtersApplied?: Record<string, string[]>;
    searchIndex?: string;
    searchIndexPosition?: number;
    pageConfigComponent?: PageConfigComponentTrackingData;
  };
  customHeadline?: JSX.Element;
  eyebrow?: {
    image?: ImageType;
  };
  customItemsGridConfig?: GridConfig;
  dataTestId?: string;
} & ModuleLayoutConfig;

const styles = {
  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  description: css`
    margin-top: var(--cds-spacing-50);
  `,

  filters: (hasVerticalLayout: boolean) => css`
    margin-bottom: var(--cds-spacing-100);
    ${hasVerticalLayout ? 'margin-top: var(--cds-spacing-150);' : ''}

    && {
      gap: var(--cds-spacing-100);
    }
  `,

  alignCta: css`
    margin-top: var(--cds-spacing-150);
  `,

  collectionsWrapper: css`
    width: 100%;
    align-self: flex-start;
  `,

  collectionWrapperVertical: css`
    margin-top: calc(-1 * var(--cds-spacing-300));

    ${breakpoints.down('sm')} {
      margin-top: calc(-1 * var(--cds-spacing-200));
    }

    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,

  collectionWrapperHorizontal: css`
    // this is to override the min-width applied to CDS product cards
    // to prevent the product cards from overlapping with each other
    .cds-ProductCard-grid {
      min-width: unset !important;
    }
  `,

  collections: css`
    margin-top: calc(-1 * var(--cds-spacing-400));
    margin-bottom: calc(-1 * var(--cds-spacing-400));

    .cds-ProductCard-grid {
      min-width: unset;
    }
  `,

  collectionListContainer: css`
    && {
      ${breakpoints.up('sm')} {
        ul {
          margin: calc(var(--cds-spacing-100) * -1);
          width: calc(100% + var(--cds-spacing-200));
        }
      }
    }
  `,

  button: css`
    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,

  chip: css`
    &.cds-Chip-root {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-background-primary);
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-blue-700);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-blue-700);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-primary);
    }
  `,

  imageFilterContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    white-space: nowrap;
  `,
  marginTop600: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-600);
    }
  `,
  marginTop1000: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-1000);
    }
  `,
  marginTop1400: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-1400);
    }
  `,
  noResultsFound: css`
    margin-top: var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-400);
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    p {
      margin-top: var(--cds-spacing-100);
    }
  `,
  customContentPanelStyles: {
    default: (hasFilters: boolean) => css`
      && {
        ${hasFilters
          ? `padding-bottom: var(--cds-spacing-100);

        ${breakpoints.down('xs')} {
          padding-bottom: 0;
        }`
          : `padding-bottom: var(--cds-spacing-200);

          ${breakpoints.down('xs')} {
          padding-bottom: var(--cds-spacing-100);
        }
        `}
      }
    `,
    sideBySideLayout: css`
      && {
        padding-bottom: 0;
      }
    `,
  },

  customImagePanelStyles: {
    default: css`
      ${breakpoints.down('xs')} {
        && {
          padding-top: 0;
        }
      }
    `,
    sideBySideLayout: css`
      && {
        padding-top: var(--cds-spacing-150);
      }
    `,
  },
};

function CollectionsModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    onFilterChange,
    backgroundColor,
    cta,
    description,
    headline,
    headlineComponent,
    collections = [],
    isCollectionsLoading,
    currentCollectionSetIndex = 0,
    numOfEntriesPerCollection,
    customCtaButton,
    customChipStyles,
    alwaysShowChipFilters,
    productCardTrackingName,
    eventingData,
    customHeadline,
    eyebrow,
    customItemsGridConfig,
    dataTestId,
  } = props;
  const layoutConfig = getModuleLayoutConfig(props);
  const isXsOrLess = useMediaQuery(breakpoints.down('xs'));
  const isMdOrLess = useMediaQuery(breakpoints.down('md'));
  const isSmOrLess = useMediaQuery(breakpoints.down('sm'));
  const trackV3 = useTracker();

  const isTwoByTenLayout =
    layout === MODULE_LAYOUTS.text2image10 ||
    layout === MODULE_LAYOUTS.image10text2 ||
    layout === MODULE_LAYOUTS.text10image2 ||
    layout === MODULE_LAYOUTS.image2text10;
  const isThreeByNineLayout = layout === MODULE_LAYOUTS.text3image9 || layout === MODULE_LAYOUTS.image9text3;
  const isSideBySideLayout = getIsSideBySideLayout(layout);

  // show one less entry if there is an odd number of entries, to prevent an orhpaned card on the last row
  const numOfEntriesPerCollectionToDisplay =
    showAllEntries && numOfEntriesPerCollection && numOfEntriesPerCollection % 2 !== 0 && isSmOrLess
      ? numOfEntriesPerCollection - 1
      : numOfEntriesPerCollection;

  const isHorizontal = isHorizontalLayout({ layout });

  const isMobileListViewMdBreakpoint =
    !!numOfEntriesPerCollectionToDisplay && numOfEntriesPerCollectionToDisplay <= 2 && !isHorizontal && isMdOrLess;

  const itemsGridConfig: GridConfig =
    customItemsGridConfig ?? (isHorizontal ? { lg: 4, md: 4, sm: 6, xs: 12 } : { lg: 3, md: 3, sm: 6, xs: 12 });
  const collectionsToDisplay = numOfEntriesPerCollectionToDisplay
    ? collections.map((collection) => ({
        ...collection,
        entities: collection.entities.slice(0, numOfEntriesPerCollectionToDisplay),
      }))
    : collections;

  const isInverted = isInvertedColor(backgroundColor ?? '');
  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  let button: React.ReactNode;

  if (customCtaButton) {
    button = customCtaButton;
  } else {
    button =
      cta?.text && cta?.target ? (
        <div css={styles.alignCta}>
          <Button
            component="a"
            variant={variantOfCta}
            icon={cta.showCtaArrowIcon || cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
            href={cta.target}
            onClick={cta.onClick}
            css={cta.variant === 'ghost' ? null : styles.button}
            fullWidth={isXsOrLess}
            edgeAlign={isXsOrLess ? undefined : 'start'}
          >
            {cta.text}
          </Button>
        </div>
      ) : null;
  }

  // only show image filters if all filters have images
  const shouldRenderImagesForFilters =
    filters?.reduce((acc, filter) => {
      return filter.filterImage ? acc + 1 : acc;
    }, 0) === filters?.length;
  const hasFilters = !!filters && (filters.length > 1 || !!alwaysShowChipFilters);

  const collectionCount = collectionsToDisplay.reduce((count, collection) => {
    return count + (collection.entities?.length ?? 0);
  }, 0);

  const getCustomPanelStyles = (panelType: 'content' | 'image') => {
    if (!isSideBySideLayout) {
      if (panelType === 'content') {
        return [styles.customContentPanelStyles.default(hasFilters)];
      } else {
        return [styles.customImagePanelStyles.default];
      }
    }

    const styleSet = panelType === 'content' ? styles.customContentPanelStyles : styles.customImagePanelStyles;
    if ((isTwoByTenLayout && isMdOrLess) || (isThreeByNineLayout && isSmOrLess)) {
      return [styleSet.sideBySideLayout];
    }

    return [];
  };

  const renderImage = () => {
    return (
      <div
        css={[
          styles.collectionsWrapper,
          isHorizontal ? styles.collectionWrapperHorizontal : styles.collectionWrapperVertical,
        ]}
      >
        <TabContext value={currentCollectionSetIndex.toString()}>
          {hasFilters && (
            <Grid
              container
              aria-label={_t('Filter By')}
              css={styles.filters(!isSideBySideLayout)}
              role="tablist"
              wrap="wrap"
              direction="row"
            >
              {filters &&
                filters.map(({ filterName, id, filterImage, eventingData: filterEventingData }, idx) => {
                  let tabContents: React.ReactText;
                  if (shouldRenderImagesForFilters) {
                    // @ts-expect-error Chip only accepts ReactText, but this JSX works as a child.
                    tabContents = (
                      <div css={styles.imageFilterContainer}>
                        <Image {...filterImage} description="" width={24} height={24} />
                        <Typography2 variant="subtitleSmall" component="span">
                          {filterName}
                        </Typography2>
                      </div>
                    );
                  } else {
                    tabContents = filterName ?? '';
                  }

                  const handleClick = () => {
                    trackV3('click_button', {
                      carousel: {
                        id,
                        name: filterName,
                        recommender: collections?.[currentCollectionSetIndex]?.recommender,
                        ...(filterEventingData?.carouselFilter ? { filter: filterEventingData.carouselFilter } : {}),
                      },
                      button: {
                        name: 'collections_filter_chip',
                        ...(eventingData?.pageConfigComponent
                          ? { pageConfigComponent: eventingData.pageConfigComponent }
                          : {}),
                      },
                    });
                    onFilterChange?.(idx, filterName);
                  };

                  const chip = (
                    <TrackedChip
                      key={id}
                      selected={currentCollectionSetIndex === idx}
                      aria-selected={currentCollectionSetIndex === idx}
                      aria-checked={undefined}
                      onClick={handleClick}
                      css={shouldRenderImagesForFilters ? styles.chip : customChipStyles}
                      aria-label={filterName}
                      role="tab"
                      aria-controls="collections-results"
                      trackingName="collections_filter_chip"
                    >
                      {tabContents}
                    </TrackedChip>
                  );

                  if (shouldRenderImagesForFilters) {
                    return <AlwaysLightMode key={id}>{chip}</AlwaysLightMode>;
                  }

                  return chip;
                })}
            </Grid>
          )}
        </TabContext>
        <div
          css={styles.collections}
          id="collections-results"
          role="tabpanel"
          data-e2e={isCollectionsLoading ? 'collections-loading' : 'collections-loaded'}
        >
          {!isCollectionsLoading && collectionCount === 0 && filters?.length ? (
            <div css={styles.noResultsFound}>
              <Typography2 variant="subtitleLarge" component="h3">
                {_t('No results found for "#{filterName}"', {
                  filterName: filters[currentCollectionSetIndex]?.filterName,
                })}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="p">
                {_t('Try changing your filters')}
              </Typography2>
            </div>
          ) : (
            <div css={styles.collectionListContainer}>
              <CollectionsContainer
                eventingData={{
                  ...eventingData,
                  ...(filters?.[currentCollectionSetIndex]?.eventingData?.carouselFilter
                    ? { carouselFilter: filters?.[currentCollectionSetIndex]?.eventingData?.carouselFilter }
                    : {}),
                }}
                productCardTrackingName={productCardTrackingName}
                collections={collectionsToDisplay}
                isCollectionsLoading={isCollectionsLoading}
                itemsGridConfig={itemsGridConfig}
                hideLoadingTitle
                showAllEntries={showAllEntries ?? false}
                renderTitle={() => null}
                cardSpacing={{ xs: 8, sm: 16 }}
              />
            </div>
          )}
        </div>
        <Hidden {...(isTwoByTenLayout || isMobileListViewMdBreakpoint ? { lgUp: true } : { mdUp: true })}>
          {button}
        </Hidden>
      </div>
    );
  };

  const getMarginTopStyle = () => {
    const breakpoint = isTwoByTenLayout ? 'lg' : 'md';
    if (hasFilters && numOfEntriesPerCollectionToDisplay != null) {
      if (numOfEntriesPerCollectionToDisplay <= 2) {
        return styles.marginTop600(breakpoint);
      } else {
        return styles.marginTop1400(breakpoint);
      }
    }
    return styles.marginTop1000(breakpoint);
  };

  const renderContent = () => {
    if (isCollectionsLoading && !headline && !customHeadline) {
      return (
        <div>
          <Skeleton data-testid="skeleton" width="70%" height={40} />
        </div>
      );
    }
    return (
      <div css={isSideBySideLayout ? getMarginTopStyle() : null}>
        {eyebrow?.image && (
          <div css={styles.eyebrowSpacing}>
            <Eyebrow image={eyebrow.image} />
          </div>
        )}
        {customHeadline || (
          <Typography2
            variant={isSideBySideLayout ? 'titleXSmall' : 'subtitleLarge'}
            component={headlineComponent ?? 'h2'}
            color="inherit"
          >
            {headline}
          </Typography2>
        )}
        {description && (
          <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}
        <Hidden {...(isTwoByTenLayout || isMobileListViewMdBreakpoint ? { mdDown: true } : { smDown: true })}>
          {button}
        </Hidden>
      </div>
    );
  };

  return (
    <ModuleLayout
      renderContent={renderContent}
      renderImage={renderImage}
      dataTestId={dataTestId}
      assembly={{
        ...layoutConfig,
        customContentPanelStyles: getCustomPanelStyles('content'),
        customImagePanelStyles: getCustomPanelStyles('image'),
      }}
    />
  );
}

export default CollectionsModule;
